
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import Actions from '../../../../components/docs/Actions';
import ContentSwitcher from '../../../../components/docs/ContentSwitcher';
import SampleCode from '../../../../components/docs/plaidoms/SampleCode';
export const metadata = {
  toc: true,
  layout: 'guide',
  description: 'Evaluate a manually entered account using Plaid network data',
  preloadImages: [{
    src: '/assets/img/docs/docs-link-overview.png',
    type: 'image/webp'
  }]
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Image = makeShortcode("Image");
const Callout = makeShortcode("Callout");
const MDXCodeBlock = makeShortcode("MDXCodeBlock");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title='Database Insights and Match (legacy)' subtitle='Evaluate a manually entered account using Plaid network data
' mdxType="Header" />
    <Image src='/assets/img/docs/auth/embedded-search-database-insights.gif' alt="Payment method selection page showing bank options with search. Car details, insurance info, and payment summary are displayed." caption='The Database Insights end-user flow with Embedded Institution Search enabled.' mdxType="Image" />
    <h4 {...{
      "id": "overview"
    }}>{`Overview`}</h4>
    <Callout warning mdxType="Callout">
      <p>{`Database Insights and Database Match have been replaced with Database Auth. The documentation provided here is for the use of customers maintaining support of existing legacy Database Insights integrations.`}</p>
    </Callout>
    <p>{`Database Insights (legacy) can increase conversion by providing instant account verification without requiring users to link a bank account via credentials. End users choosing the manual Database Insights path will not be required to log in to their financial institution and instead can enter their account and routing number manually. `}</p>
    <p>{`Database Insights verifies account and routing numbers by checking the information provided against Plaid's known account numbers, leveraging Plaid's database of over 200 million verified accounts. If no match is found, Plaid will check the account number format against known usages by the institution associated with the given routing number. Database Insights will provide a verification status of 'pass', 'pass with caution', or 'fail' and a set of attributes that contributed to that status, such as whether a match was found or whether Plaid fell back to checking account number formats. `}</p>
    <p>{`Database Insights does not verify that the user has access to the bank account and Database Insights also does not fully guarantee that the account exists, especially in a 'pass with caution' scenario. For these reasons, Database Insights should only be enabled where there is a low risk of fraud or ACH returns. Examples of use cases where Database Insights may be appropriate include bill payment, rent collection, business to business payments, or subscription payments.`}</p>
    <Callout mdxType="Callout">
      <p>{`Approximately 30% of Items verified by Database Insights can also be verified by `}<inlineCode parentName="p">{`/identity/match`}</inlineCode>{` or `}<inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{`. For more details, see `}<a parentName="p" {...{
          "href": "/docs/identity/#identity-match"
        }}>{`Identity Match`}</a>{` and `}<a parentName="p" {...{
          "href": "/docs/signal"
        }}>{`Signal Transaction Scores`}</a>{`. All Items verified with Database Insights are also compatible with account ownership identity verification via `}<a parentName="p" {...{
          "href": "/docs/identity/identity-document-upload"
        }}>{`Identity Document Upload`}</a>{`.`}</p>
    </Callout>
    <p>{`Database Insights and `}<a parentName="p" {...{
        "href": "/docs/link/embedded-institution-search"
      }}>{`Embedded Institution Search`}</a>{` are both designed to increase adoption of ACH payment methods and are frequently used together. Database Insights is also fully compatible with the standard, non-Embedded Institution Search Link flow.`}</p>
    <h5 {...{
      "id": "database-insights-flow"
    }}>{`Database Insights flow`}</h5>
    <ol>
      <li parentName="ol">{`Starting on a page in your app, the user clicks an action that opens Plaid Link.`}</li>
      <li parentName="ol">{`Inside of Plaid Link, the user selects an option to enter the manual verification flow
and provides their account and routing number.`}</li>
      <li parentName="ol">{`Once the user has submitted their information, Link closes and returns a `}<inlineCode parentName="li">{`public_token`}</inlineCode>{` within the `}<inlineCode parentName="li">{`onSuccess`}</inlineCode>{` callback. `}</li>
      <li parentName="ol">{`Call `}<inlineCode parentName="li">{`/item/public_token/exchange`}</inlineCode>{` to exchange the `}<inlineCode parentName="li">{`public_token`}</inlineCode>{` for an `}<inlineCode parentName="li">{`access_token`}</inlineCode>{`, then call `}<inlineCode parentName="li">{`/auth/get`}</inlineCode>{` to obtain the account numbers and the verification results.`}</li>
      <li parentName="ol">{`Based on the values of the `}<inlineCode parentName="li">{`verification_status`}</inlineCode>{` and `}<inlineCode parentName="li">{`verification_insights`}</inlineCode>{` fields returned by `}<inlineCode parentName="li">{`/auth/get`}</inlineCode>{`, make a decision whether to proceed with the account information for ACH or to reject the account information as unverified.`}</li>
    </ol>
    <Callout warning mdxType="Callout">
      <p>{`When using other flows, customers using a `}<a parentName="p" {...{
          "href": "/docs/auth/partnerships"
        }}>{`processor partner`}</a>{` do not typically need to call `}<inlineCode parentName="p">{`/auth/get`}</inlineCode>{`, and can directly call `}<inlineCode parentName="p">{`/processor/token/create`}</inlineCode>{` instead. However, if you are using Database Insights with a processor partner, you must call `}<inlineCode parentName="p">{`/auth/get`}</inlineCode>{` and check the value of the `}<inlineCode parentName="p">{`verification_status`}</inlineCode>{` and/or `}<inlineCode parentName="p">{`verification_insights`}</inlineCode>{` fields before passing a processor token to the partner. `}</p>
    </Callout>
    <h4 {...{
      "id": "implementation-steps"
    }}>{`Implementation steps`}</h4>
    <h5 {...{
      "id": "create-a-link_token"
    }}>{`Create a link_token`}</h5>
    <p>{`Create a `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` with the following parameters:`}</p>
    <ul>
      <li parentName="ul">{`The `}<inlineCode parentName="li">{`products`}</inlineCode>{` array should include only `}<inlineCode parentName="li">{`auth`}</inlineCode>{` or `}<inlineCode parentName="li">{`transfer`}</inlineCode>{` as a product when using Database Insights. While in most cases additional products can be added to existing Plaid Items, Items created with Database Insights are an exception and cannot be used with any Plaid products other than Auth, Transfer, Signal, or Identity Match.`}</li>
    </ul>
    <Callout mdxType="Callout">
      <p>{`Approximately 30% of Items verified by Database Insights can also be verified by `}<inlineCode parentName="p">{`/identity/match`}</inlineCode>{` or Signal. For more details, see `}<a parentName="p" {...{
          "href": "/docs/identity/#identity-match"
        }}>{`Identity Match`}</a>{` and `}<a parentName="p" {...{
          "href": "/docs/signal/signal-rules/#data-availability-limitations"
        }}>{`Signal`}</a>{`. If using Identity Match or Signal in this way, they should be added to the Item via the `}<inlineCode parentName="p">{`required_if_supported_products`}</inlineCode>{`, `}<inlineCode parentName="p">{`optional_products`}</inlineCode>{`, or `}<inlineCode parentName="p">{`additional_consented_products`}</inlineCode>{` fields rather than the `}<inlineCode parentName="p">{`products`}</inlineCode>{` array.`}</p>
    </Callout>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`country_codes`}</inlineCode>{` should be set to `}<inlineCode parentName="li">{`['US']`}</inlineCode>{` or `}<inlineCode parentName="li">{`['CA']`}</inlineCode>{` – Database Insights is currently only available in the United States or Canada.`}</li>
      <li parentName="ul">{`The `}<inlineCode parentName="li">{`auth`}</inlineCode>{` object should specify `}<inlineCode parentName="li">{`"database_insights_enabled": true`}</inlineCode>{`. `}</li>
      <li parentName="ul">{`(Optional) Within the `}<inlineCode parentName="li">{`auth`}</inlineCode>{` object, specify `}<inlineCode parentName="li">{`"auth_type_select_enabled": true`}</inlineCode>{` in order to enable `}<a parentName="li" {...{
          "href": "/docs/auth/coverage/flow-options/#adding-manual-verification-entry-points-with-auth-type-select"
        }}>{`Auth Type Select`}</a>{`, which will surface the manual entry point for Database Insights on the default Link screen. If Auth Type Select is not enabled, credential-based flows will be the primary UI flow and the Database Insights entry point will only appear as a fallback, except when using `}<a parentName="li" {...{
          "href": "/docs/link/embedded-institution-search/"
        }}>{`Embedded Institution Search`}</a>{`.`}</li>
    </ul>
    <p>{`Database Insights cannot be used in the same session as Database Match, Same-Day Micro-deposits, or Instant Micro-deposits. If any of those flows are explicitly enabled in the `}<inlineCode parentName="p">{`auth`}</inlineCode>{` object alongside Database Insights, an error will occur when calling `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{`. If they are implicitly enabled due to account default settings, they will be overridden by enabling Database Insights. `}</p>
    <ContentSwitcher.Content>
      <MDXCodeBlock type='server' title='Database Insights Configuration' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/link/token/create \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "user": {"client_user_id": "\${UNIQUE_USER_ID}"},
  "client_name": "Plaid App",
  "products": ["auth"],
  "country_codes": ["US"],
  "language": "en",
  "webhook": "https://sample-web-hook.com",
  "redirect_uri": "https://domainname.com/oauth-page.html",
  "auth": {
    "database_insights_enabled": true
  }
}'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`link_token_create_request = Plaid::LinkTokenCreateRequest.new(
  {
    user: {
      client_user_id: "user-id",
    },
    client_name: 'Plaid Test App',
    products: ['auth'],
    country_codes: ['US'],
    language: "en",
    webhook: 'https://webhook.sample.com',
    link_customization_name: "default",
    auth: {
        database_insights_enabled: true
    },
  }
)
response = client.link_token_create(
  link_token_create_request
)
link_token = response.link_token
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`const request: LinkTokenCreateRequest = {
  user: { client_user_id: new Date().getTime().toString() },
  client_name: 'Plaid App',
  products: [Products.Auth],
  country_codes: [CountryCode.Us],
  webhook: 'https://webhook.sample.com',
  language: 'en',
  auth: {
    database_insights_enabled: true
  },
};
try {
  const response = await plaidClient.linkTokenCreate(request);
  const linkToken = response.data.link_token;
} catch (error) {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`LinkTokenCreateRequestAuth auth = new LinkTokenCreateRequestAuth()
    .databaseInsightsEnabled(true);

LinkTokenCreateRequest request = new LinkTokenCreateRequest()
    .user(user)
    .clientName("Test app")
    .products(Arrays.asList(Products.AUTH))
    .countryCodes(Arrays.asList(CountryCode.US))
    .language("en")
    .webhook("https://example.com/webhook")
    .linkCustomizationName("default")
    .accountFilters(accountFilters)
    .auth(auth);
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = LinkTokenCreateRequest(
    products=[Products('auth')],
    client_name="Plaid Test App",
    country_codes=[CountryCode('US')],
    language='en',
    webhook='https://sample-webhook-uri.com',
    link_customization_name='default',
    user=LinkTokenCreateRequestUser(
        client_user_id='user-id',
    ),
    auth=LinkTokenCreateRequestAuth(
        database_insights_enabled=True,
    )
)

response = client.link_token_create(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`user := plaid.LinkTokenCreateRequestUser{
  ClientUserId: "user-id",
}

request := plaid.NewLinkTokenCreateRequest(
  "Plaid Test",
  "en",
  []plaid.CountryCode{plaid.COUNTRYCODE_US},
)
request.SetUser(user)
request.SetProducts([]plaid.Products{plaid.PRODUCTS_AUTH})
request.SetLinkCustomizationName("default")
request.SetWebhook("https://webhook-uri.com")
var databaseInsightsEnabled bool = true
request.SetAuth(plaid.LinkTokenCreateRequestAuth{
 DatabaseInsightsEnabled: &databaseInsightsEnabled,
})
resp, _, err := client.PlaidApi.LinkTokenCreate(ctx).LinkTokenCreateRequest(*request).Execute()
if err != nil {
  panic(err)
}

linkToken := resp.GetLinkToken()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`user := plaid.LinkTokenCreateRequestUser{ClientUserID: "user-id"}

request := &plaid.LinkTokenCreateRequest{
    ClientName:   "Plaid Test",
    Language:     "en",
    CountryCodes: []plaid.CountryCode{plaid.CountryCodeUs},
}
request.User = &user
request.Products = []plaid.Products{plaid.ProductsAuth}
request.LinkCustomizationName = plaid.String("default")
request.Webhook = plaid.String("https://webhook-uri.com")
var databaseInsightsEnabled bool = true
request.Auth = &plaid.LinkTokenCreateRequestAuth{DatabaseInsightsEnabled: &databaseInsightsEnabled}
resp, err := client.Link.CreateToken(ctx, request)
if err != nil {
    panic(err)
}

linkToken := resp.GetLinkToken()
`}</code></pre>
      </MDXCodeBlock>
    </ContentSwitcher.Content>
    <h5 {...{
      "id": "initialize-link-with-a-link_token"
    }}>{`Initialize Link with a link_token`}</h5>
    <p>{`After creating a `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` for the `}<inlineCode parentName="p">{`auth`}</inlineCode>{` product, use it to initialize Plaid Link.`}</p>
    <p>{`When the user successfully inputs their account and routing numbers, the `}<inlineCode parentName="p">{`onSuccess()`}</inlineCode>{` callback
function will return a `}<inlineCode parentName="p">{`public_token`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "switcher=false #App.js",
        "switcher": "false",
        "#App.js": true
      }}>{`const linkHandler = Plaid.create({
  // Fetch a link_token configured for 'auth' from your app server
  token: (await $.post('/create_link_token')).link_token,
  onSuccess: (public_token, metadata) => {
    // Send the public_token and connected accounts to your app server
    $.post('/exchange_public_token', {
      publicToken: public_token,
      accounts: metadata.accounts,
    });

    metadata = {
      ...,
      link_session_id: String,
      institution: {
        name: null, // name is always null for Database Insights
        institution_id: null // institution_id is always null for Database Insights
      },
      accounts: [{
        id: 'vzeNDwK7KQIm4yEog683uElbp9GRLEFXGK98D',
        mask: '9606',
        name: null,
        type: 'depository',
        subtype: 'checking',
        verification_status: ''
      }]
    }
  },
  // ...
});

// Open Link on user-action
linkHandler.open();
`}</code></pre>
    <h5 {...{
      "id": "exchange-the-public-token"
    }}>{`Exchange the public token`}</h5>
    <p>{`In your own backend server, call the `}<inlineCode parentName="p">{`/item/public_token/exchange`}</inlineCode>{`
endpoint with the Link `}<inlineCode parentName="p">{`public_token`}</inlineCode>{` received in the `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` callback to
obtain an `}<inlineCode parentName="p">{`access_token`}</inlineCode>{`. Persist the returned `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` and `}<inlineCode parentName="p">{`item_id`}</inlineCode>{` in your database
in relation to the user.`}</p>
    <MDXCodeBlock type='server' title='Exchange token request' mdxType="MDXCodeBlock">
      <pre><code parentName="pre" {...{
          "className": "language-bash"
        }}>{`curl -X POST https://sandbox.plaid.com/item/public_token/exchange \\
  -H 'Content-Type: application/json' \\
  -d '{
    "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
    "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
    "public_token": "public-sandbox-b0e2c4ee-a763-4df5-bfe9-46a46bce993d"
  }'
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-node"
        }}>{`// publicToken and accountID are sent from your app to your backend-server
const accountID = 'vzeNDwK7KQIm4yEog683uElbp9GRLEFXGK98D';
const publicToken = 'public-sandbox-b0e2c4ee-a763-4df5-bfe9-46a46bce993d';

// Obtain an access_token from the Link public_token
try {
  const response = await client.itemPublicTokenExchange({
    public_token: publicToken,
  });
  const accessToken = response.data.access_token;
} catch (err) {
  // handle error
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`# publicToken and accountID are sent from your app to your backend-server
accountID = 'vzeNDwK7KQIm4yEog683uElbp9GRLEFXGK98D'
publicToken = 'public-sandbox-b0e2c4ee-a763-4df5-bfe9-46a46bce993d'

# Obtain an access_token from the Link public_token
token_request = ItemPublicTokenExchangeRequest(
      public_token=publicToken)
token_response = client.item_public_token_exchange(token_request)
accessToken = token_response['access_token']
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-ruby"
        }}>{`# publicToken and accountID are sent from your app to your backend-server
accountID = 'vzeNDwK7KQIm4yEog683uElbp9GRLEFXGK98D'
publicToken = 'public-sandbox-b0e2c4ee-a763-4df5-bfe9-46a46bce993d'

# Obtain an access_token from the Link public_token
request = Plaid::ItemPublicTokenExchangeRequest.new(
  {
    public_token: publicToken
  }
)
token_response = client.item_public_token_exchange(request)
access_token = token_response.access_token
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-java"
        }}>{`// publicToken and accountID are sent from your app to your backend-server
String accountID = "vzeNDwK7KQIm4yEog683uElbp9GRLEFXGK98D";
String publicToken = "public-sandbox-b0e2c4ee-a763-4df5-bfe9-46a46bce993d";

// Obtain an access_token from the Link public_token
ItemPublicTokenExchangeRequest tokenRequest = new ItemPublicTokenExchangeRequest()
  .publicToken(publicToken);

Response<ItemPublicTokenExchangeResponse> tokenResponse = plaidClient
  .itemPublicTokenExchange(tokenRequest)
  .execute();
String accessToken = tokenResponse.body().getAccessToken();
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go"
        }}>{`// publicToken and accountID are sent from your app to your backend-server
accountID := "vzeNDwK7KQIm4yEog683uElbp9GRLEFXGK98D"
publicToken := "public-sandbox-b0e2c4ee-a763-4df5-bfe9-46a46bce993d"

// Obtain an access_token from the Link public_token
exchangePublicTokenResp, _, err := client.PlaidApi.ItemPublicTokenExchange(ctx).ItemPublicTokenExchangeRequest(
  *plaid.NewItemPublicTokenExchangeRequest(publicToken),
).Execute()
accessToken := exchangePublicTokenResp.GetAccessToken()
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go",
          "metastring": "sdk=fern",
          "sdk": "fern"
        }}>{`// publicToken and accountID are sent from your app to your backend-server
accountID := "vzeNDwK7KQIm4yEog683uElbp9GRLEFXGK98D"
publicToken := "public-sandbox-b0e2c4ee-a763-4df5-bfe9-46a46bce993d"

// Obtain an access_token from the Link public_token
exchangePublicTokenResp, err := client.Item.ExchangePublicToken(ctx, &plaid.ItemPublicTokenExchangeRequest{PublicToken: publicToken})
accessToken := exchangePublicTokenResp.GetAccessToken()
`}</code></pre>
    </MDXCodeBlock>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "switcher=false #Exchange token response",
        "switcher": "false",
        "#Exchange": true,
        "token": true,
        "response": true
      }}>{`{
  "access_token": "access-sandbox-5cd6e1b1-1b5b-459d-9284-366e2da89755",
  "item_id": "M5eVJqLnv3tbzdngLDp9FL5OlDNxlNhlE55op",
  "request_id": "m8MDnv9okwxFNBV"
}
`}</code></pre>
    <h5 {...{
      "id": "fetch-auth-data-and-verification-results"
    }}>{`Fetch Auth data and verification results`}</h5>
    <p>{`Next, we can retrieve Auth data, along with the results of the Database Insights verification check, by calling `}<inlineCode parentName="p">{`/auth/get`}</inlineCode>{`.`}</p>
    <MDXCodeBlock type='server' title='Auth request' mdxType="MDXCodeBlock">
      <pre><code parentName="pre" {...{
          "className": "language-bash"
        }}>{`curl -X POST https://sandbox.plaid.com/auth/get \\
  -H 'Content-Type: application/json' \\
  -d '{
    "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
    "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
    "access_token": "access-sandbox-5cd6e1b1-1b5b-459d-9284-366e2da89755"
  }'
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-node"
        }}>{`const accessToken = 'access-sandbox-5cd6e1b1-1b5b-459d-9284-366e2da89755';

// Instantly fetch Auth numbers
const request: AuthGetRequest = {
  access_token: accessToken,
};
try {
  const response = await client.authGet(request);
  const numbers = response.data.numbers;
} catch (err) {
  // handle error
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`access_token = 'access-sandbox-5cd6e1b1-1b5b-459d-9284-366e2da89755'

# Instantly fetch Auth numbers
auth_request = AuthGetRequest(access_token=access_token)
auth_response = client.auth_get(auth_request)
numbers = auth_response['numbers']
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-ruby"
        }}>{`access_token = 'access-sandbox-5cd6e1b1-1b5b-459d-9284-366e2da89755';

# Instantly fetch Auth numbers
auth_get_request = Plaid::AuthGetRequest.new
auth_get_request.access_token = access_token

auth_response = client.auth_get(auth_get_request)
numbers = auth_response.numbers
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-java"
        }}>{`String accessToken = "access-sandbox-5cd6e1b1-1b5b-459d-9284-366e2da89755";

// Instantly fetch Auth numbers
AuthGetRequest authGetRequest = new AuthGetRequest()
  .accessToken(accessToken);

Response<AuthGetResponse> authResponse = client()
  .authGet(authGetRequest)
  .execute();

if (authResponse.isSuccessful()) {
  AuthGetNumbers numbers = authResponse.body().getNumbers();
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go"
        }}>{`accessToken := "access-sandbox-5cd6e1b1-1b5b-459d-9284-366e2da89755"

// Instantly fetch Auth numbers
authGetResp, _, err := client.PlaidApi.AuthGet(ctx).AuthGetRequest(
  *plaid.NewAuthGetRequest(accessToken),
).Execute()
numbers := authGetResp.GetNumbers()
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go",
          "metastring": "sdk=fern",
          "sdk": "fern"
        }}>{`accessToken := "access-sandbox-5cd6e1b1-1b5b-459d-9284-366e2da89755"

// Instantly fetch Auth numbers
authGetResp, err := client.Auth.Get(ctx, &plaid.AuthGetRequest{AccessToken: accessToken})
numbers := authGetResp.GetNumbers()
`}</code></pre>
    </MDXCodeBlock>
    
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#Auth response",
        "#Auth": true,
        "response": true
      }}>{`{
  "numbers": {
    "ach": [
      {
        "account_id": "vzeNDwK7KQIm4yEog683uElbp9GRLEFXGK98D",
        "account": "9900009606",
        "routing": "011401533",
        "wire_routing": "021000021"
      }
    ],
    "eft": [],
    "international": [],
    "bacs": []
  },
  "accounts": [
    {
      "account_id": "vzeNDwK7KQIm4yEog683uElbp9GRLEFXGK98D",
      "balances": { Object },
      "mask": "9606",
      "name": null,
      "official_name": null,
      "verification_status": "database_insights_pass",
      "verification_insights": { 
        "network_status": {
          "has_numbers_match": true,
          "is_numbers_match_verified": true
        },
        "previous_returns": {
          "has_previous_administrative_return": false
        },
        "account_number_format": "valid"
      },
      "subtype": "checking",
      "type": "depository"
    }
  ],
  "item": { Object },
  "request_id": "m8MDnv9okwxFNBV"
}
`}</code></pre>
    <h4 {...{
      "id": "making-a-risk-determination"
    }}>{`Making a risk determination`}</h4>
    <p>{`The results of the Database Insights verification can be found in the `}<inlineCode parentName="p">{`verification_status`}</inlineCode>{` and `}<inlineCode parentName="p">{`verification_insights`}</inlineCode>{` fields returned by `}<inlineCode parentName="p">{`/auth/get`}</inlineCode>{`. Based on the values in these fields, you will make a business decision on whether to accept the account numbers as verified, take additional risk mitigation steps, or reject the account numbers as unverified.`}</p>
    <h4 {...{
      "id": "testing-database-insights-in-sandbox"
    }}>{`Testing Database Insights in Sandbox`}</h4>
    <p>{`For test credentials that can be used to test Database Insights in the Sandbox environment, see `}<a parentName="p" {...{
        "href": "/docs/auth/coverage/testing#testing-database-auth-or-database-insights"
      }}>{`Testing Database Auth or Database Insights`}</a>{`.`}</p>
    <h4 {...{
      "id": "database-match"
    }}>{`Database Match`}</h4>
    <Callout warning mdxType="Callout">
      <p>{`Database Insights and Database Match have been replaced with Database Auth. The documentation provided here is for the use of customers maintaining existing legacy Database Insights and Database Match integrations.`}</p>
    </Callout>
    <p><a parentName="p" {...{
        "href": "#database-match"
      }}>{`Database Match`}</a>{` enables instant manual account verification without the need for micro-deposits, instead relying on Plaid's database of known account numbers. When provided as an alternative to Same-Day Micro-deposits, Database Match can increase conversion, as the user may be able to verify instantly, without having to return to Plaid to verify their micro-deposit codes. `}</p>
    <p>{`Database Match will present the user with the option to manually add a bank account in Link by providing their name, account number, and routing number. Plaid will then check this information against its network of over 200 million known bank accounts. Approximately 30% of user bank accounts can be verified via Database Match. If a match is not found, Database Match will fall back to routing the user to a manual micro-deposit flow, either Instant Micro-deposits or Same-Day Micro-deposits.`}</p>
    <Callout warning mdxType="Callout">
      <p>{`Database Match can be used to verify the validity of the bank account being linked, but it does not verify that the end user has access to the bank account. To mitigate account takeover risk with Database Match, it can be paired with `}<a parentName="p" {...{
          "href": "/docs/identity"
        }}>{`Identity Match`}</a>{` to verify the user's information, such as phone number and address, on the linked account.`}</p>
    </Callout>
    <h5 {...{
      "id": "database-match-flow"
    }}>{`Database Match flow`}</h5>
    <ol>
      <li parentName="ol">{`Starting on a page in your app, the user clicks an action that opens Plaid Link.`}</li>
      <li parentName="ol">{`Inside of Plaid Link, the user selects an option to enter the manual verification flow
and provides their legal name, account and routing number.`}</li>
      <li parentName="ol">{`Plaid will confirm the account number, routing number, and name match a previously verified account. `}</li>
      <li parentName="ol">{`If a match is confirmed, Link closes with a `}<inlineCode parentName="li">{`public_token`}</inlineCode>{` and a verification status of `}<inlineCode parentName="li">{`database_matched`}</inlineCode>{`.`}</li>
      <li parentName="ol">{`If there is no match, the user will be prompted to enter the Instant or Same-Day Micro-deposits flow. `}</li>
    </ol>
    <p>{`When these steps are complete, you can call `}<inlineCode parentName="p">{`/item/public_token/exchange`}</inlineCode>{` to obtain an `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` for calling endpoints such as `}<inlineCode parentName="p">{`/auth/get`}</inlineCode>{` or `}<inlineCode parentName="p">{`/processor/token/create`}</inlineCode>{`.`}</p>
    <h5 {...{
      "id": "database-match-implementation-steps"
    }}>{`Database Match implementation steps`}</h5>
    <p>{`To enable Database Match, use the same settings as Same-Day Micro-deposits. You can then enable the feature in one of two ways:`}</p>
    <ul>
      <li parentName="ul">{`When calling `}<inlineCode parentName="li">{`/link/token/create`}</inlineCode>{`, set `}<inlineCode parentName="li">{`auth.database_match_enabled: true`}</inlineCode>{`.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://dashboard.plaid.com/account-verification"
        }}>{`In the Dashboard Account Verification pane`}</a>{`, enable Database Match.`}</li>
    </ul>
    <p>{`Database Match cannot be used in the same session as `}<a parentName="p" {...{
        "href": "/docs/auth/coverage/database"
      }}>{`Database Insights`}</a>{`. If Database Insights is explicitly enabled in the `}<inlineCode parentName="p">{`auth`}</inlineCode>{` object alongside Database Match, an error will occur when calling `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{`.`}</p>
    <ContentSwitcher.Content>
      <MDXCodeBlock type='server' title='Database Match Configuration' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/link/token/create \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "user": {"client_user_id": "\${UNIQUE_USER_ID}"},
  "client_name": "Plaid App",
  "products": ["auth"],
  "country_codes": ["US"],
  "language": "en",
  "webhook": "https://sample-web-hook.com",
  "redirect_uri": "https://domainname.com/oauth-page.html",
  "auth": {
    "database_match_enabled": true,
    "same_day_microdeposits_enabled": true
  }
}'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`link_token_create_request = Plaid::LinkTokenCreateRequest.new(
  {
    user: {
      client_user_id: "user-id",
    },
    client_name: 'Plaid Test App',
    products: ['auth'],
    country_codes: ['US'],
    language: "en",
    webhook: 'https://webhook.sample.com',
    link_customization_name: "default",
    auth: {
        database_match_enabled: true,
        same_day_microdeposits_enabled: true
    },
  }
)
response = client.link_token_create(
  link_token_create_request
)
link_token = response.link_token
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`const request: LinkTokenCreateRequest = {
  user: { client_user_id: new Date().getTime().toString() },
  client_name: 'Plaid App',
  products: [Products.Auth],
  country_codes: [CountryCode.Us],
  webhook: 'https://webhook.sample.com',
  language: 'en',
  auth: {
    database_match_enabled: true,
    same_day_microdeposits_enabled: true
  },
};
try {
  const response = await plaidClient.linkTokenCreate(request);
  const linkToken = response.data.link_token;
} catch (error) {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`LinkTokenCreateRequestAuth auth = new LinkTokenCreateRequestAuth()
    .databaseMatchEnabled(true);

LinkTokenCreateRequest request = new LinkTokenCreateRequest()
    .user(user)
    .clientName("WonderWallet")
    .products(Arrays.asList(Products.AUTH))
    .countryCodes(Arrays.asList(CountryCode.US))
    .language("en")
    .webhook("https://example.com/webhook")
    .linkCustomizationName("default")
    .accountFilters(accountFilters)
    .auth(auth);
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = LinkTokenCreateRequest(
    products=[Products('auth')],
    client_name="Plaid Test App",
    country_codes=[CountryCode('US')],
    language='en',
    webhook='https://sample-webhook-uri.com',
    link_customization_name='default',
    user=LinkTokenCreateRequestUser(
        client_user_id='user-id',
    ),
    auth=LinkTokenCreateRequestAuth(
        database_match_enabled=True,
        same_day_microdeposits_enabled=True,

    )
)

response = client.link_token_create(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`user := plaid.LinkTokenCreateRequestUser{
  ClientUserId: "user-id",
}

request := plaid.NewLinkTokenCreateRequest(
  "Plaid Test",
  "en",
  []plaid.CountryCode{plaid.COUNTRYCODE_US},
)
request.SetUser(user)
request.SetProducts([]plaid.Products{plaid.PRODUCTS_AUTH})
request.SetLinkCustomizationName("default")
request.SetWebhook("https://webhook-uri.com")
var databaseMatchEnabled bool = true
var sameDayMicrodepositsEnabled bool = true
request.SetAuth(plaid.LinkTokenCreateRequestAuth{
 DatabaseMatchEnabled: &databaseMatchEnabled,
 SameDayMicrodepositsEnabled: &sameDayMicrodepositsEnabled,
})
resp, _, err := client.PlaidApi.LinkTokenCreate(ctx).LinkTokenCreateRequest(*request).Execute()
if err != nil {
  panic(err)
}

linkToken := resp.GetLinkToken()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`user := plaid.LinkTokenCreateRequestUser{ClientUserID: "user-id"}

request := &plaid.LinkTokenCreateRequest{
    ClientName:   "Plaid Test",
    Language:     "en",
    CountryCodes: []plaid.CountryCode{plaid.CountryCodeUs},
}
request.User = &user
request.Products = []plaid.Products{plaid.ProductsAuth}
request.LinkCustomizationName = plaid.String("default")
request.Webhook = plaid.String("https://webhook-uri.com")
var databaseMatchEnabled bool = true
var sameDayMicrodepositsEnabled bool = true
request.Auth = &plaid.LinkTokenCreateRequestAuth{
    DatabaseMatchEnabled:        &databaseMatchEnabled,
    SameDayMicrodepositsEnabled: &sameDayMicrodepositsEnabled,
}
resp, err := client.Link.CreateToken(ctx, request)
if err != nil {
    panic(err)
}

linkToken := resp.GetLinkToken()
`}</code></pre>
      </MDXCodeBlock>
    </ContentSwitcher.Content>
    <p>{`When calling `}<inlineCode parentName="p">{`/auth/get`}</inlineCode>{`, the returned object will have a `}<inlineCode parentName="p">{`verification_status`}</inlineCode>{` value of `}<inlineCode parentName="p">{`database_matched`}</inlineCode>{` as an indication that the user's data was verified through Database Match.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#Auth response for Database Match",
        "#Auth": true,
        "response": true,
        "for": true,
        "Database": true,
        "Match": true
      }}>{`{
  "numbers": {
    "ach": [
      {
        "account_id": "vzeNDwK7KQIm4yEog683uElbp9GRLEFXGK98D",
        "account": "1111222233330000",
        "routing": "011401533",
        "wire_routing": "021000021"
      }
    ],
    ...
  },
  "accounts": [
    {
      "account_id": "vzeNDwK7KQIm4yEog683uElbp9GRLEFXGK98D",
      "balances": { Object },
      "mask": "0000",
      "name": "Checking...0000",
      "official_name": null,
      "verification_status": "database_matched",
      "subtype": "checking",
      "type": "depository"
    }
  ],
  ...
}
`}</code></pre>
    <h5 {...{
      "id": "testing-database-match-in-sandbox"
    }}>{`Testing Database Match in Sandbox`}</h5>
    <p>{`For test credentials that can be used to test Database Match in the Sandbox environment, see `}<a parentName="p" {...{
        "href": "/docs/auth/coverage/testing#testing-database-match"
      }}>{`Testing Database Match`}</a>{`.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "overview",
        level: 4,
        title: "Overview",
      },
{
        id: "implementation-steps",
        level: 4,
        title: "Implementation steps",
      },
{
        id: "making-a-risk-determination",
        level: 4,
        title: "Making a risk determination",
      },
{
        id: "testing-database-insights-in-sandbox",
        level: 4,
        title: "Testing Database Insights in Sandbox",
      },
{
        id: "database-match",
        level: 4,
        title: "Database Match",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
